import { Button } from "@openanalyst-ai/ui/button"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { IconButton } from "@openanalyst-ai/ui/icon-button"
import { Mark } from "@openanalyst-ai/ui/logo"
import { createMemo, Show } from "solid-js"
import { usePlatform } from "@/context/platform"
import { StarterIllustration } from "@/components/workspace-selector"
import {
  starterPanelItemForTemplate,
  type ProjectStarterTemplateID,
} from "@/services/project-starters"

const DEFAULT_TEMPLATE_ID = "openanalyst-shopify-manager" satisfies ProjectStarterTemplateID

export function OnboardingWorkspaceIntroDialog(props: {
  templateId?: ProjectStarterTemplateID | null
  directory?: string | null
  /** When provided, the primary button becomes "Start first run" and hands off
   * to the first-run dialog (Dialog 2) instead of just closing. */
  onContinue?: () => void
}) {
  const dialog = useDialog()
  const platform = usePlatform()
  const templateId = () => props.templateId ?? DEFAULT_TEMPLATE_ID
  const starter = createMemo(() => starterPanelItemForTemplate(templateId()))
  const openTenX = () => {
    platform.openLink("https://app.openanalyst.com")
    dialog.close()
  }

  return (
    <Dialog
      fit
      transition
      class="oa-codex-intro-dialog oa-onboarding-workspace-intro"
    >
      <div class="oa-codex-intro-shell">
        <div class="oa-codex-intro-visual" data-variant="workspace">
          <IconButton
            type="button"
            icon="close"
            variant="ghost"
            aria-label="Close"
            class="oa-codex-intro-close"
            onClick={() => dialog.close()}
          />
          <div class="oa-codex-intro-project-preview">
            <StarterIllustration templateId={templateId()} />
          </div>
        </div>
        <div class="oa-codex-intro-copy">
          <div class="oa-codex-intro-logo">
            <Mark class="oa-codex-intro-logo-mark" />
          </div>
          <h2 class="oa-codex-intro-title">
            Your <span class="oa-codex-intro-title-highlight">OpenAnalyst</span> workspace is ready
          </h2>
          <p>
            <strong>{starter()?.title ?? "Your starter project"}</strong>
            {": "}
            {starter()?.description ??
              "OpenAnalyst has created your starter project and will use your account model for the first run."}
          </p>
          <Show when={props.directory}>
            <span class="oa-codex-intro-path" title={props.directory ?? undefined}>{props.directory}</span>
          </Show>
        </div>
        <div class="oa-codex-intro-actions">
          <Button type="button" variant="secondary" size="large" onClick={openTenX}>
            Visit OpenAnalyst.com
          </Button>
          <Button
            type="button"
            variant="primary"
            size="large"
            onClick={() => {
              dialog.close()
              props.onContinue?.()
            }}
          >
            {props.onContinue ? "Start first run" : "Explore OpenAnalyst"}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
